import { Component, type ErrorInfo, type ReactNode } from 'react';

import { motion } from 'framer-motion';

import { Button } from '@/components';
import { ERROR_CONTENT } from '@/content';
import { Icon } from '@/utils/icons';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error', { error, errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="from-primary-50 mx-auto flex min-h-[400px] flex-col items-center justify-center bg-gradient-to-br to-white p-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="shadow-premium rounded-3xl bg-white p-4">
            <div className="mb-6 flex items-center justify-center">
              <div className="rounded-full bg-red-50 p-4">
                <Icon name="AlertCircle" size={48} className="text-red-500" />
              </div>
            </div>
            <h1 className="font-display mb-3 text-2xl font-bold text-neutral-800">
              {ERROR_CONTENT.boundary.title}
            </h1>
            <p className="font-body mb-4 leading-relaxed text-neutral-600">
              {ERROR_CONTENT.boundary.description}
            </p>
            {import.meta.env.MODE === 'development' && (
              <div className="mx-auto mb-6 max-w-5xl overflow-hidden rounded-2xl border border-red-100 bg-red-50">
                <div className="bg-red-100/50 px-3 py-1 text-xs font-bold text-red-500 uppercase">
                  Development Info
                </div>
                <pre className="max-h-44 overflow-auto p-3 text-left text-xs text-red-600">
                  {this.state.error?.stack || this.state.error?.toString()}
                </pre>
              </div>
            )}
            <div className="flex justify-center">
              <Button
                onClick={this.handleReset}
                variant="primary"
                size="sm"
                leftIcon={<Icon name="RefreshCw" size={18} />}>
                {ERROR_CONTENT.boundary.button}
              </Button>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
